import React from "react";
import { Helmet } from "react-helmet-async";
import { ChevronDown, MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";

const FaqLoisirs = ({ setOpenIndex, openIndex, toggleFAQ, idCategorie }) => {
  const faqList = [
    {
      question: () => (
        <strong>
          1. Pourquoi utiliser Deal.mg pour acheter des articles de loisirs ?
        </strong>
      ),
      answer: () => (
        <p>
          Deal.mg offre un environnement sécurisé et une communauté active de
          passionnés. Acheter d'occasion vous permet de trouver du matériel de
          qualité à un prix bien inférieur au neuf.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          2. Comment assurer la sécurité lors de l'achat d'articles de loisirs ?
        </strong>
      ),
      answer: () => (
        <p>
          Pour vos équipements de{" "}
          <strong>sport ou jeux vidéo d'occasion</strong>, demandez au vendeur
          une vidéo de démonstration ou des photos de détails via téléphone pour
          confirmer le bon fonctionnement du matériel. Pour les transactions
          entre provinces, privilégiez les transporteurs locaux fiables et
          validez les modalités d'envoi avant tout transfert de fonds. Une
          discussion directe sur l'historique d'utilisation de l'objet permet
          souvent de confirmer le sérieux de l'annonce.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          3. Comment vendre mes articles de sport ou de musique rapidement ?
        </strong>
      ),
      answer: () => (
        <p>
          Prenez des photos claires et rédigez une description précise de l'état
          de votre matériel. L'utilisation de notre outil d'IA aide à générer
          une description optimisée pour le <strong>SEO loisirs</strong>.
        </p>
      ),
    },
    {
      question: () => (
        <strong>4. Puis-je trouver des jeux vidéo récents en occasion ?</strong>
      ),
      answer: () => (
        <p>
          Oui, de nombreux utilisateurs revendent leurs jeux rapidement après
          les avoir terminés. Utilisez la barre de recherche pour trouver les
          titres les plus récents.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          5. Comment vérifier le bon fonctionnement d'une console de jeux ?
        </strong>
      ),
      answer: () => (
        <p>
          Demandez au vendeur de tester la console devant vous. Vérifiez que la
          console se connecte bien, que les manettes fonctionnent et qu'il n'y a
          pas de surchauffe.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          6. Comment fixer le juste prix pour mes instruments de musique ?
        </strong>
      ),
      answer: () => (
        <p>
          Comparez votre instrument avec des modèles similaires publiés sur
          Deal.mg, en tenant compte de la marque, de l'âge et de l'état général
          de l'instrument.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          7. Existe-t-il des risques de contrefaçon pour les articles de loisirs
          ?
        </strong>
      ),
      answer: () => (
        <p>
          Comme sur toutes les plateformes, la vigilance est de mise. Pour les
          articles de marque (instruments, matériel de sport), demandez la
          facture d'origine ou vérifiez les numéros de série.
        </p>
      ),
    },
    {
      question: () => (
        <strong>8. Comment signaler un article de loisirs suspect ?</strong>
      ),
      answer: () => (
        <p>
          Si vous détectez une annonce frauduleuse, cliquez sur le bouton «
          Signaler » pour alerter notre équipe de modération.
        </p>
      ),
    },
    {
      question: () => (
        <strong>9. Puis-je louer du matériel de loisirs sur Deal.mg ?</strong>
      ),
      answer: () => (
        <p>
          Oui, la catégorie permet également de proposer du matériel à la
          location (ex: matériel de sonorisation, vélos). Précisez-le bien dans
          le titre et la description de votre annonce.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          10. Comment Deal.mg sécurise-t-il l'achat de matériel de sport ou
          d'instruments ?
        </strong>
      ),
      answer: () => (
        <p>
          Deal.mg facilite la mise en relation sécurisée. Nous recommandons de
          {" "}<strong>tester le bon fonctionnement du matériel</strong> (vélos,
          instruments, consoles) avec le vendeur et d'éviter les transferts
          d'argent à distance sans garantie de réception.
        </p>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>Sports, musique et jeux à Madagascar | Deal.mg</title>
        <meta
          name="description"
          content="Découvrez les meilleures offres d'articles de sport, jeux vidéo et instruments de musique d'occasion à Madagascar sur Deal.mg."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "Product",
            name: "Sports, musique et jeux à Madagascar",
            image: ["https://deal.mg/logo.png"],
            description:
              "Vélos, consoles, jeux et instruments de musique d'occasion à Madagascar sur Deal.mg",
            sku: idCategorie,
            offers: {
              "@type": "Offer",
              url: `https://deal.mg/article/resultat?idCategorie=${idCategorie}`,
              availability: "https://schema.org/InStock",
            },
          })}
        </script>
      </Helmet>

      <h1 className="text-center">
        Sports, musique et jeux : loisirs à Madagascar
      </h1>

      <p>
        Découvrez un univers de passion sur Deal.mg, la plateforme de référence
        pour les <strong>loisirs à Madagascar</strong>. Que vous soyez amateur
        de sport, mélomane, gamer ou collectionneur, trouvez l'équipement idéal
        d'occasion à <strong>Antananarivo, Toamasina</strong> et dans tout
        Madagascar. En choisissant de publier votre annonce sur notre
        plateforme, vous bénéficiez d'une visibilité maximale auprès d'une
        communauté active et engagée, tout en assurant la sécurité de vos
        échanges.
      </p>

      <h2>Sport et fitness</h2>
      <p>
        Équipez-vous sans vous ruiner. Trouvez des vélos, des équipements de
        musculation, des kits de camping ou des accessoires de plongée
        d'occasion. Faire du <strong>sport à Madagascar</strong> devient
        accessible à tous grâce aux bonnes affaires de notre communauté.
      </p>

      <h2>Jeux vidéo et multimédia</h2>
      <p>
        Gamers, accédez aux dernières consoles (PlayStation, Xbox, Nintendo) et
        aux jeux les plus populaires en seconde main. Nos annonces de{" "}
        <strong>jeux vidéo occasion</strong> vous permettent de renouveler votre
        collection à prix réduit.
      </p>

      <h2>Instruments de musique et matériel de fête</h2>
      <p>
        Musiciens, trouvez votre guitare, votre piano ou votre matériel de
        sonorisation. Pour vos événements, louez ou achetez des équipements de
        fête. Les meilleures opportunités pour vos{" "}
        <strong>loisirs à Madagascar </strong> sont sur{" "}
        <Link to="/">
          <strong>Deal.mg</strong>
        </Link>
        . N'oubliez pas de consulter la section{" "}
        <Link to="/electronique/tous?categorie=6">
          <strong>Électronique</strong>
        </Link>{" "}
        pour trouver des téléviseurs afin d'enrichir votre expérience multimédia
        à domicile.
      </p>

      <h2>Vendez rapidement vos équipements inutilisés</h2>
      <p>
        Vous n'utilisez plus votre matériel de sport ou vos jeux ?
        Transformez-les en argent. Publiez une annonce détaillée en utilisant
        notre outil d'IA pour optimiser votre description et garantir une
        {" "}<strong>vente rapide</strong>.
      </p>

      <br />

      <h2 className="support_faq_title">
        <MessageSquare size={20} className="support_faq_title_icon" />
        FAQ
      </h2>

      {faqList.map((item, index) => (
        <div
          key={index}
          className={`support_faq_item ${openIndex === index ? "open" : ""}`}
        >
          <div
            className="support_faq_question"
            onClick={() => toggleFAQ(index)}
          >
            <span>{item.question()}</span>
            <ChevronDown
              className={`support_faq_arrow ${
                openIndex === index ? "support_faq_rotate_180" : ""
              }`}
              size={20}
            />
          </div>

          <div
            className={`support_faq_answer_container${openIndex === index ? "active" : ""}`}
          >
            <div className="support_faq_answer">{item.answer()}</div>
          </div>
        </div>
      ))}
    </>
  );
};

export default FaqLoisirs;
